<template>
    <validation-observer ref="simpleRules">
        <b-form>
            <b-row>

                <!-- ===================== DATOS DEL ALMACÉN ===================== -->
                <b-col cols="12">
                    <h5 class="mb-1">Datos del almacén</h5>
                </b-col>

                <!-- Alias -->
                <b-col md="4">
                    <b-form-group
                        label="Alias"
                        label-for="alias"
                    >
                        <validation-provider
                            #default="{ errors }"
                            rules="required"
                            name="alias del almacén"
                        >
                            <b-form-input
                                id="alias"
                                v-model="form.alias"
                                placeholder="Ej. ATE, SURCO ..."
                                :state="errors.length > 0 ? false : null"
                                trim
                            />
                            <small class="text-danger">{{ errors[0] }}</small>
                        </validation-provider>
                    </b-form-group>
                </b-col>

                <!-- Dirección -->
                <b-col md="8">
                    <b-form-group
                        label="Dirección"
                        label-for="address"
                    >
                        <validation-provider
                            #default="{ errors }"
                            rules="required"
                            name="dirección del almacén"
                        >
                            <b-form-input
                                id="address"
                                v-model="form.address"
                                placeholder="Ej. Av. Michael Faraday 601, Ate, Lima"
                                :state="errors.length > 0 ? false : null"
                                trim
                            />
                            <small class="text-danger">{{ errors[0] }}</small>
                        </validation-provider>
                    </b-form-group>
                </b-col>

                <!-- Departamento -->
                <b-col md="4">
                    <b-form-group label="Departamento" label-for="department">
                        <b-form-input
                            id="department"
                            v-model="form.department"
                            readonly
                        />
                    </b-form-group>
                </b-col>

                <!-- Provincia -->
                <b-col md="4">
                    <b-form-group label="Provincia" label-for="province">
                        <b-form-input
                            id="province"
                            v-model="form.province"
                            readonly
                        />
                    </b-form-group>
                </b-col>

                <!-- Distrito -->
                <b-col md="4">
                    <b-form-group label="Distrito" label-for="district">
                        <b-form-select
                            id="district"
                            v-model="form.district"
                            :options="districtOptions"
                            :disabled="loadingDistricts"
                        >
                            <template #first>
                                <b-form-select-option value="" disabled>
                                    {{ loadingDistricts ? 'Cargando distritos ...' : 'Selecciona un distrito' }}
                                </b-form-select-option>
                            </template>
                        </b-form-select>
                    </b-form-group>
                </b-col>

                <!-- Teléfono -->
                <b-col md="4">
                    <b-form-group label="Teléfono (opcional)" label-for="phone">
                        <b-form-input
                            id="phone"
                            v-model="form.phone"
                            placeholder="Ej. +51 984 783 981"
                            trim
                        />
                    </b-form-group>
                </b-col>

                <!-- Link de Google Maps -->
                <b-col md="8">
                    <b-form-group label="Link de Google Maps (opcional)" label-for="maps_url">
                        <b-form-input
                            id="maps_url"
                            v-model="form.maps_url"
                            placeholder="https://maps.app.goo.gl/..."
                            trim
                        />
                    </b-form-group>
                </b-col>

                <b-col cols="12"><hr></b-col>

                <!-- ===================== RECOJO EN TIENDA ===================== -->
                <b-col cols="12">
                    <h5 class="mb-1">Recojo en almacén</h5>
                    <b-alert variant="primary" show class="mb-1">
                        <div class="alert-body">
                            Estos datos son los que ve el cliente en el checkout y en el correo de
                            confirmación cuando elige recoger su pedido en este almacén.
                        </div>
                    </b-alert>
                </b-col>

                <!-- Permite recojo -->
                <b-col md="4">
                    <b-form-group label="¿Permite recojo de pedidos?" label-for="allows_pickup">
                        <b-form-checkbox
                            id="allows_pickup"
                            v-model="form.allows_pickup"
                            switch
                            inline
                        >
                            {{ form.allows_pickup ? 'Sí, se ofrece al cliente' : 'No, solo abastece delivery' }}
                        </b-form-checkbox>
                    </b-form-group>
                </b-col>

                <!-- Horario -->
                <b-col md="8">
                    <b-form-group label="Horario de atención">
                        <b-row>
                            <b-col cols="6" md="3">
                                <b-form-select
                                    id="schedule_day_from"
                                    v-model="form.schedule_day_from"
                                    :options="dayOptions"
                                >
                                    <template #first>
                                        <b-form-select-option :value="null" disabled>De</b-form-select-option>
                                    </template>
                                </b-form-select>
                            </b-col>
                            <b-col cols="6" md="3">
                                <b-form-select
                                    id="schedule_day_to"
                                    v-model="form.schedule_day_to"
                                    :options="dayOptions"
                                >
                                    <template #first>
                                        <b-form-select-option :value="null" disabled>A</b-form-select-option>
                                    </template>
                                </b-form-select>
                            </b-col>
                            <b-col cols="6" md="3">
                                <b-form-select
                                    id="schedule_hour_from"
                                    v-model="form.schedule_hour_from"
                                    :options="hourOptions"
                                >
                                    <template #first>
                                        <b-form-select-option :value="null" disabled>Desde</b-form-select-option>
                                    </template>
                                </b-form-select>
                            </b-col>
                            <b-col cols="6" md="3">
                                <b-form-select
                                    id="schedule_hour_to"
                                    v-model="form.schedule_hour_to"
                                    :options="hourOptions"
                                >
                                    <template #first>
                                        <b-form-select-option :value="null" disabled>Hasta</b-form-select-option>
                                    </template>
                                </b-form-select>
                            </b-col>
                        </b-row>
                        <small class="text-muted">{{ schedulePreview }}</small>
                    </b-form-group>
                </b-col>

                <!-- Tiempo de preparación -->
                <b-col md="4">
                    <b-form-group
                        label="Tiempo de preparación"
                        label-for="preparation_time_hours"
                        :description="preparationPreview"
                    >
                        <b-input-group append="horas">
                            <b-form-input
                                id="preparation_time_hours"
                                v-model="form.preparation_time_hours"
                                type="number"
                                min="1"
                                max="72"
                                step="1"
                            />
                        </b-input-group>
                    </b-form-group>
                </b-col>

                <!-- Hora de corte -->
                <b-col md="4">
                    <b-form-group
                        label="Hora de corte"
                        label-for="cutoff_time"
                        description="Después de esta hora el pedido se recoge al día siguiente."
                    >
                        <b-form-select
                            id="cutoff_time"
                            v-model="form.cutoff_time"
                            :options="hourOptions"
                        >
                            <template #first>
                                <b-form-select-option :value="null" disabled>Selecciona una hora</b-form-select-option>
                            </template>
                        </b-form-select>
                    </b-form-group>
                </b-col>

                <!-- Costo de recojo -->
                <b-col md="4">
                    <b-form-group
                        label="Costo de recojo (opcional)"
                        label-for="pickup_cost"
                        :description="pickupCostHint"
                    >
                        <b-input-group prepend="S/. ">
                            <b-form-input
                                id="pickup_cost"
                                v-model="form.pickup_cost"
                                type="number"
                                min="0"
                                step="0.01"
                                :placeholder="generalPickupCost === null ? 'Hereda el general' : `Hereda S/ ${generalPickupCost}`"
                            />
                        </b-input-group>
                    </b-form-group>
                </b-col>

                <b-col cols="12"><hr></b-col>

                <!-- ===================== ABASTECIMIENTO ===================== -->
                <b-col cols="12">
                    <h5 class="mb-1">Abastecimiento y estado</h5>
                </b-col>

                <!-- Por defecto -->
                <b-col md="4">
                    <b-form-group
                        label="¿Es el almacén por defecto?"
                        label-for="is_default"
                        description="Almacén de respaldo cuando ningún otro cubre el pedido."
                    >
                        <b-form-checkbox
                            id="is_default"
                            v-model="form.is_default"
                            switch
                            inline
                        >
                            {{ form.is_default ? 'Sí' : 'No' }}
                        </b-form-checkbox>
                    </b-form-group>
                </b-col>

                <!-- Activo -->
                <b-col md="4">
                    <b-form-group label="Estado" label-for="is_active">
                        <b-form-checkbox
                            id="is_active"
                            v-model="form.is_active"
                            switch
                            inline
                        >
                            {{ form.is_active ? 'Activo' : 'Inactivo' }}
                        </b-form-checkbox>
                    </b-form-group>
                </b-col>

                <!-- Correos de alerta -->
                <b-col md="12">
                    <b-form-group
                        label="Correos para la alerta de stock mínimo"
                        label-for="notification_emails"
                        description="Uso interno: no se muestran en la tienda. Escribe un correo y presiona Enter. Reciben el aviso cuando un producto de este almacén baja del mínimo."
                    >
                        <validation-provider
                            #default="{ errors }"
                            rules="required"
                            name="correos de alerta"
                        >
                            <b-form-tags
                                id="notification_emails"
                                v-model="form.notification_emails"
                                input-type="email"
                                separator=" ,;"
                                placeholder="Agregar correo ..."
                                remove-on-delete
                                tag-variant="primary"
                                :state="errors.length > 0 ? false : null"
                            />
                            <small class="text-danger">{{ errors[0] }}</small>
                        </validation-provider>
                    </b-form-group>
                </b-col>

                <!--
                    Integracion con The Hub: no esta contemplada por ahora. Se deja
                    comentada por si se implementa mas adelante. Los campos siguen
                    existiendo en el modelo y en el backend (sync_with_hub,
                    hub_warehouse_id, hub_courier_service_id).
                <b-col cols="12"><hr></b-col>

                ===================== THE HUB =====================
                <b-col cols="12">
                    <h5 class="mb-1">Integración con The Hub</h5>
                    <b-alert variant="warning" show class="mb-1">
                        <div class="alert-body">
                            El <strong>ID de almacén en The Hub</strong> debe coincidir con el almacén real
                            en Mintsoft. Si no lo completas, los pedidos que salgan de este almacén no se
                            podrán sincronizar y quedarán pendientes de envío manual.
                        </div>
                    </b-alert>
                </b-col>

                Sincroniza
                <b-col md="4">
                    <b-form-group label="¿Se despacha por The Hub?" label-for="sync_with_hub">
                        <b-form-checkbox
                            id="sync_with_hub"
                            v-model="form.sync_with_hub"
                            switch
                            inline
                        >
                            {{ form.sync_with_hub ? 'Sí' : 'No, se gestiona manualmente' }}
                        </b-form-checkbox>
                    </b-form-group>
                </b-col>

                Warehouse Id
                <b-col md="4">
                    <b-form-group label="ID de almacén en The Hub" label-for="hub_warehouse_id">
                        <b-form-input
                            id="hub_warehouse_id"
                            v-model="form.hub_warehouse_id"
                            type="number"
                            placeholder="Ej. 3"
                            :disabled="!form.sync_with_hub"
                        />
                    </b-form-group>
                </b-col>

                Courier Service Id
                <b-col md="4">
                    <b-form-group
                        label="ID de servicio de retiro en The Hub"
                        label-for="hub_courier_service_id"
                        description="Courier de retiro en almacén. Si lo dejas vacío se asigna courier automáticamente."
                    >
                        <b-form-input
                            id="hub_courier_service_id"
                            v-model="form.hub_courier_service_id"
                            type="number"
                            placeholder="Ej. 1016"
                            :disabled="!form.sync_with_hub"
                        />
                    </b-form-group>
                </b-col>
                -->

                <!-- Submit -->
                <b-col cols="12" class="mt-2">
                    <b-button
                        v-ripple.400="'rgba(255, 255, 255, 0.15)'"
                        variant="primary"
                        :disabled="saving"
                        @click="validationForm"
                    >
                        <b-spinner v-if="saving" small class="mr-50" />
                        {{ submitLabel }}
                    </b-button>
                    <b-button
                        v-ripple.400="'rgba(186, 191, 199, 0.15)'"
                        variant="outline-secondary"
                        class="ml-1"
                        :to="{ name: 'warehouses' }"
                    >
                        CANCELAR
                    </b-button>
                </b-col>

            </b-row>
        </b-form>
    </validation-observer>
</template>

<script>

    import {
        BRow, BCol, BForm, BFormGroup, BFormInput, BFormCheckbox, BFormTags,
        BInputGroup, BButton, BAlert, BSpinner, BFormSelect, BFormSelectOption
    } from 'bootstrap-vue';
    import axios from '@axios';
    import { ValidationProvider, ValidationObserver } from 'vee-validate';
    import { required } from '@validations';
    import Ripple from 'vue-ripple-directive';

    /* Mismos valores que valida el backend en warehouses.js: el usuario solo
       puede elegir de estas listas, así todos los almacenes quedan con el
       mismo formato. */
    const DAY_OPTIONS = ['Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado', 'Domingo'];

    const HOUR_OPTIONS = (() => {
        const list = [];
        for (let hour = 0; hour < 24; hour++) {
            list.push(String(hour).padStart(2, '0') + ':00');
            list.push(String(hour).padStart(2, '0') + ':30');
        }
        return list;
    })();

    const emptyWarehouse = () => ({
        alias: '',
        address: '',
        department: 'LIMA',
        province: 'LIMA',
        district: '',
        phone: '',
        maps_url: '',
        schedule_day_from: null,
        schedule_day_to: null,
        schedule_hour_from: null,
        schedule_hour_to: null,
        preparation_time_hours: '',
        cutoff_time: null,
        pickup_cost: '',
        is_default: false,
        is_active: true,
        allows_pickup: true,
        notification_emails: [],
        sync_with_hub: false,   /* The Hub deshabilitado: el campo esta oculto en el form */
        hub_warehouse_id: '',
        hub_courier_service_id: ''
    });

    export default {
        components: {
            BRow,
            BCol,
            BForm,
            BFormGroup,
            BFormInput,
            BFormCheckbox,
            BFormTags,
            BInputGroup,
            BButton,
            BAlert,
            BSpinner,
            BFormSelect,
            BFormSelectOption,
            ValidationProvider,
            ValidationObserver
        },
        directives: {
            Ripple
        },
        props: {
            warehouseData: {
                type: Object,
                default: null
            },
            submitLabel: {
                type: String,
                default: 'GUARDAR'
            },
            saving: {
                type: Boolean,
                default: false
            }
        },
        data() {
            return {
                required,
                form: emptyWarehouse(),
                districtOptions: [],
                loadingDistricts: false,
                generalPickupCost: null
            }
        },
        watch: {
            warehouseData: {
                immediate: true,
                handler(value) {
                    if (!value) return;

                    /* Los campos numéricos opcionales se muestran vacíos, no como "null". */
                    const blankIfNull = field => (value[field] === null || value[field] === undefined ? '' : value[field]);

                    this.form = Object.assign(emptyWarehouse(), value, {
                        pickup_cost: blankIfNull('pickup_cost'),
                        hub_warehouse_id: blankIfNull('hub_warehouse_id'),
                        hub_courier_service_id: blankIfNull('hub_courier_service_id'),
                        notification_emails: value.notification_emails || []
                    });

                    this.matchDistrictOption();
                }
            }
        },
        computed: {
            dayOptions() {
                return DAY_OPTIONS;
            },
            hourOptions() {
                return HOUR_OPTIONS;
            },
            /* Muestra el texto exacto que verá el cliente, que es el que arma
               el backend con estos mismos cuatro campos. */
            schedulePreview() {
                const { schedule_day_from: dayFrom, schedule_day_to: dayTo } = this.form;
                const { schedule_hour_from: hourFrom, schedule_hour_to: hourTo } = this.form;

                if (!dayFrom || !dayTo || !hourFrom || !hourTo) {
                    return 'Completa los cuatro campos para definir el horario.';
                }

                const days = dayFrom === dayTo ? dayFrom : `${dayFrom} a ${dayTo}`;

                return `El cliente verá: "${days} de ${hourFrom} a ${hourTo}"`;
            },
            preparationPreview() {
                const hours = Number(this.form.preparation_time_hours);

                if (!Number.isFinite(hours) || hours <= 0) return 'Cuántas horas tarda en estar listo el pedido.';

                return `El cliente verá: "Listo para recoger en ${hours === 1 ? '1 hora' : `${hours} horas`}"`;
            },
            pickupCostHint() {
                return this.generalPickupCost === null ?
                    'Si lo dejas vacío se cobra el costo general de recojo.' :
                    `Si lo dejas vacío se cobra S/ ${this.generalPickupCost}, el costo general de recojo.`;
            }
        },
        created() {
            this.fetchDistricts();
            this.fetchGeneralPickupCost();
        },
        methods: {
            /* Distritos de Lima Metropolitana. El almacen guarda el nombre como
               texto, asi que las opciones son los nombres en mayusculas. */
            async fetchDistricts() {
                this.loadingDistricts = true;

                try {
                    const { data } = await axios.get('/locations/districts');
                    this.districtOptions = data.map(district => district.name.toUpperCase());
                    this.matchDistrictOption();
                } catch (error) {
                    this.districtOptions = [];
                } finally {
                    this.loadingDistricts = false;
                }
            },
            /* Costo general de recojo: es el que se cobra cuando el almacén no
               define uno propio. Vive en el tipo de entrega RECOJO EN ALMACEN. */
            async fetchGeneralPickupCost() {
                try {
                    const { data } = await axios.get('/delivery-types');
                    const pickup = data.delivery_types.find(type => type.locations === undefined || !type.locations.length);

                    this.generalPickupCost = pickup && pickup.amount !== undefined ? pickup.amount : null;
                } catch (error) {
                    this.generalPickupCost = null;
                }
            },
            /* Los almacenes antiguos guardaron el distrito con otro formato
               ("ATE" frente a "Ate"), asi que se empareja sin distinguir
               mayusculas para que el select lo preseleccione. */
            matchDistrictOption() {
                if (!this.districtOptions.length || !this.form.district) return;

                const current = String(this.form.district).trim().toLowerCase();
                this.form.district = this.districtOptions.find(option => option.toLowerCase() === current) || '';
            },
            reset() {
                this.form = emptyWarehouse();
                this.$refs.simpleRules.reset();
            },
            validationForm() {
                this.$refs.simpleRules.validate()
                    .then(success => {
                        if (success) this.$emit('submit', Object.assign({}, this.form));
                    });
            }
        }
    }

</script>
